/**
 * useNotificationToast
 *
 * Ascolta l'array `notifications` dal context e mostra un Sonner toast
 * ogni volta che arriva una notifica nuova (non letta) in realtime.
 *
 * Distingue le notifiche "iniziali" (fetch al mount) dalle nuove realtime
 * confrontando l'array precedente — mostra toast solo per aggiunte dopo il
 * primo caricamento.
 *
 * Throttle: max 1 toast ogni 2s per evitare spam.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationContext';

const TYPE_EMOJI = {
    broadcast:       '📢',
    past_due:        '⚠️',
    new_sub:         '🎉',
    payment_ok:      '✅',
    deadline:        '📅',
    new_contract:    '📝',
    request_pending: '📋',
    message:         '💬',
};

export function useNotificationToast() {
    const { notifications } = useNotifications();
    const navigate          = useNavigate();
    const prevIdsRef        = useRef(null);   // Set di IDs visti finora
    const lastToastTime     = useRef(0);
    const initialized       = useRef(false);  // Ignora il primo batch (fetch mount)

    useEffect(() => {
        if (notifications.length === 0) return;

        // Prima esecuzione: memorizza gli ID iniziali senza mostrare toast
        if (!initialized.current) {
            prevIdsRef.current = new Set(notifications.map(n => n.id));
            initialized.current = true;
            return;
        }

        const currentIds = new Set(notifications.map(n => n.id));
        const prevIds    = prevIdsRef.current || new Set();

        // Trova le notifiche nuove (non presenti nel batch precedente)
        const newNotifs = notifications.filter(n => !prevIds.has(n.id) && !n.read);

        if (newNotifs.length > 0) {
            const now = Date.now();
            if (now - lastToastTime.current > 2000) {
                // Se ci sono più notifiche nuove, mostra solo la prima + contatore
                const first   = newNotifs[0];
                const emoji   = TYPE_EMOJI[first.type] || '🔔';
                const extra   = newNotifs.length > 1 ? ` (+${newNotifs.length - 1} altre)` : '';

                toast(
                    `${emoji} ${first.title}${extra}`,
                    {
                        description: first.message?.slice(0, 80),
                        duration:    5000,
                        action: first.link ? {
                            label:   'Apri',
                            onClick: () => navigate(first.link),
                        } : {
                            label:   'Vedi tutte',
                            onClick: () => navigate('/app/notifications'),
                        },
                    }
                );

                lastToastTime.current = now;
            }
        }

        prevIdsRef.current = currentIds;
    }, [notifications, navigate]);
}
